import { EllipsisCell } from "@/components/ellipsis-cell/EllipsisCell";
import { formatDate } from "@/lib/formatters/DateFormatter";
import {
  formatThousandsAndDecimals,
  formatThousandsCheckNull,
} from "@/lib/formatters/numbers";
import { Box, Column } from "@stenajs-webui/core";
import {
  TableCell,
  TableHeadItem,
  TableHeadRow,
  TableRow,
} from "@stenajs-webui/grid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAvailableViewportHeight } from "../hooks/UseAvailableViewportHeight";
import { useElementSize } from "../hooks/UseElementSize";
import {
  PriceListSortKey,
  PriceListSortState,
  PriceListSortValue,
  PriceListSortValueResolver,
  sortPriceListRows,
} from "../lib/sortPriceList";
import { rowWindow } from "../lib/rowWindow";
import { formatUnaccompanied } from "../lib/priceListValues";
import { useResolveVehicleTypeName } from "../hooks/UseResolveVehicleTypeName";
import { PriceListRow } from "../types/PriceListRow";

const ROW_HEIGHT_PX = 40;
const HEADER_LABEL_OFFSET = 12;
const OVERSCAN_ROWS = 45;
const DEFAULT_VIEWPORT_ROWS = 30;

interface PriceListColumn {
  key: PriceListSortKey;
  label: string;
  minWidthPx: number;
  value: (row: PriceListRow) => string;
  sortValue?: (row: PriceListRow) => PriceListSortValue;
  bold?: boolean;
}

const columnStyle = (col: PriceListColumn) => ({
  flexGrow: 1,
  flexShrink: 0,
  flexBasis: `${col.minWidthPx}px`,
  minWidth: `${col.minWidthPx}px`,
});

const totalMinWidth = (columns: PriceListColumn[]) =>
  columns.reduce((total, col) => total + col.minWidthPx, 0);

const createColumns = (
  resolveVehicleTypeName: (code: string) => string,
  compact: boolean
): PriceListColumn[] => [
  {
    key: "routeAgreementNumber",
    label: compact ? "Agr. no" : "Agreement",
    minWidthPx: compact ? 90 : 130,
    value: (row) => String(row.routeAgreementNumber),
  },
  {
    key: "customerIndex",
    label: compact ? "Cust." : "Customer",
    minWidthPx: compact ? 90 : 120,
    value: (row) => row.customerIndex,
  },
  {
    key: "validFrom",
    label: compact ? "Valid fr." : "Valid from",
    minWidthPx: compact ? 95 : 140,
    value: (row) => formatDate(row.validFrom),
  },
  {
    key: "validTo",
    label: "Valid to",
    minWidthPx: compact ? 95 : 130,
    value: (row) => formatDate(row.validTo),
  },
  {
    key: "route",
    label: "Route",
    minWidthPx: compact ? 75 : 110,
    value: (row) => row.route,
  },
  {
    key: "sailingCategory",
    label: compact ? "Cat." : "Sailing cat.",
    minWidthPx: compact ? 60 : 140,
    value: (row) => row.sailingCategory,
  },
  {
    key: "routeAgreementName",
    label: compact ? "Route agr." : "Route agreement",
    minWidthPx: compact ? 150 : 220,
    value: (row) => row.routeAgreementName,
  },
  {
    key: "vehicleType",
    label: compact ? "Vehicle" : "Vehicle type",
    minWidthPx: compact ? 100 : 140,
    value: (row) => resolveVehicleTypeName(row.vehicleType),
    sortValue: (row) => resolveVehicleTypeName(row.vehicleType),
  },
  {
    key: "length",
    label: compact ? "Len (m)" : "Length (m)",
    minWidthPx: compact ? 75 : 130,
    value: (row) => formatThousandsAndDecimals(row.length, 0),
  },
  {
    key: "weightFrom",
    label: compact ? "Wt from" : "Weight from",
    minWidthPx: compact ? 85 : 140,
    value: (row) => formatThousandsCheckNull(row.weightFrom),
  },
  {
    key: "weightTo",
    label: compact ? "Wt to" : "Weight to",
    minWidthPx: compact ? 75 : 130,
    value: (row) => formatThousandsCheckNull(row.weightTo),
  },
  {
    key: "unaccompanied",
    label: "Unacc",
    minWidthPx: compact ? 100 : 140,
    value: (row) => formatUnaccompanied(row.unaccompanied),
    sortValue: (row) => formatUnaccompanied(row.unaccompanied),
  },
  {
    key: "routeAgreementCurrency",
    label: compact ? "Curr." : "Currency",
    minWidthPx: compact ? 65 : 115,
    value: (row) => row.routeAgreementCurrency,
  },
  {
    key: "price",
    label: "Price",
    minWidthPx: compact ? 90 : 120,
    value: (row) => formatThousandsAndDecimals(row.price),
    bold: true,
  },
];

const FULL_LAYOUT_WIDTH_PX = totalMinWidth(
  createColumns((code) => code, false)
);

interface Props {
  rows: PriceListRow[];
  content?: React.ReactNode;
  resetKey?: unknown;
}

export const PriceListTable = ({ rows, content, resetKey }: Props) => {
  const resolveVehicleTypeName = useResolveVehicleTypeName();

  const { ref: containerRef, maxHeightPx } = useAvailableViewportHeight();
  const { widthPx: containerWidthPx, heightPx: containerHeightPx } =
    useElementSize(containerRef);

  const compact =
    containerWidthPx > 0 && containerWidthPx < FULL_LAYOUT_WIDTH_PX;

  const columns = useMemo(
    () => createColumns(resolveVehicleTypeName, compact),
    [resolveVehicleTypeName, compact]
  );

  const [sort, setSort] = useState<PriceListSortState>({
    key: "customerIndex",
    desc: false,
  });

  const sortValue = useCallback<PriceListSortValueResolver>(
    (row, key) => {
      const column = columns.find((col) => col.key === key);
      return column?.sortValue
        ? column.sortValue(row)
        : (row[key] as PriceListSortValue);
    },
    [columns]
  );

  const sortedRows = useMemo(
    () => sortPriceListRows(rows, sort, sortValue),
    [rows, sort, sortValue]
  );

  const toggleSort = (key: PriceListSortKey) =>
    setSort((prev) =>
      prev.key === key ? { key, desc: !prev.desc } : { key, desc: false }
    );

  const [scrollTop, setScrollTop] = useState(0);
  const headRowRef = useRef<HTMLDivElement>(null);

  const { heightPx: measuredHeadHeightPx } = useElementSize(headRowRef);
  const headHeightPx = measuredHeadHeightPx || ROW_HEIGHT_PX;

  const pendingFrameRef = useRef<number | null>(null);

  const readScrollTop = useCallback(() => {
    if (pendingFrameRef.current != null) {
      return;
    }
    pendingFrameRef.current = requestAnimationFrame(() => {
      pendingFrameRef.current = null;
      const currentScrollTop = containerRef.current?.scrollTop ?? 0;
      setScrollTop(
        Math.floor(currentScrollTop / ROW_HEIGHT_PX) * ROW_HEIGHT_PX
      );
    });
  }, [containerRef]);

  useEffect(
    () => () => {
      if (pendingFrameRef.current != null) {
        cancelAnimationFrame(pendingFrameRef.current);
      }
    },
    []
  );

  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0 });
    setScrollTop(0);
  }, [resetKey, sort, containerRef]);

  const { start, end, topSpacerHeight, bottomSpacerHeight } = rowWindow({
    scrollTop: scrollTop - headHeightPx,
    viewportHeightPx:
      containerHeightPx || maxHeightPx || DEFAULT_VIEWPORT_ROWS * ROW_HEIGHT_PX,
    rowHeightPx: ROW_HEIGHT_PX,
    rowCount: sortedRows.length,
    overscan: OVERSCAN_ROWS,
  });

  const renderedRows = sortedRows.slice(start, end);

  return (
    <Box
      ref={containerRef}
      overflow={"auto"}
      maxHeight={
        maxHeightPx !== undefined ? `${maxHeightPx}px` : "calc(100vh - 320px)"
      }
      width={"100%"}
      onScroll={readScrollTop}
    >
      <Column>
        <div
          ref={headRowRef}
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            flexShrink: 0,
            background: "var(--lhds-color-ui-50)",
          }}
        >
          <TableHeadRow>
            {columns.map((col) => (
              <div
                key={col.key}
                data-testid={`price-list-header-${col.key}`}
                role={"button"}
                tabIndex={0}
                aria-label={`Sort by ${col.label}`}
                onClick={() => toggleSort(col.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleSort(col.key);
                  }
                }}
                style={{
                  ...columnStyle(col),
                  display: "flex",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <TableHeadItem
                  label={col.label}
                  justifyContent={"flex-start"}
                  selected={sort.key === col.key}
                  arrow={
                    sort.key === col.key
                      ? sort.desc
                        ? "down"
                        : "up"
                      : undefined
                  }
                  style={{
                    whiteSpace: "nowrap",
                    paddingLeft: HEADER_LABEL_OFFSET,
                    flexGrow: 1,
                    minWidth: 0,
                  }}
                />
              </div>
            ))}
          </TableHeadRow>
        </div>
        {content ? (
          <Box position={"sticky"} left={0} width={containerWidthPx}>
            {content}
          </Box>
        ) : (
          <>
            {topSpacerHeight > 0 && (
              <div style={{ height: topSpacerHeight, flexShrink: 0 }} />
            )}
            {/*
              Keyed by position in the sorted list rather than by the row's own
              fields. Nothing the API returns is unique on its own -- the same
              agreement can quote the same route, vehicle, length and weight
              band over two validity periods -- and a duplicate key would let
              React reuse a row against the wrong row while scrolling. The
              rows are pure functions of their row, so reusing a node for a
              different row is exactly what should happen here.
            */}
            {renderedRows.map((row, index) => (
              <TableRow
                key={start + index}
                height={`${ROW_HEIGHT_PX}px`}
                style={{ flexShrink: 0 }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    justifyContent={"center"}
                    style={columnStyle(col)}
                  >
                    <EllipsisCell
                      value={col.value(row)}
                      variant={col.bold ? "bold" : undefined}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {bottomSpacerHeight > 0 && (
              <div style={{ height: bottomSpacerHeight, flexShrink: 0 }} />
            )}
          </>
        )}
      </Column>
    </Box>
  );
};
